import React, { useEffect, useState } from "react";
import { Modal, ModalBody, Button, Input, Form, Label, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import * as Yup from "yup";
import {
  addAuditingParameter,
  getLegendsList,
  updateAuditingParameter,
} from "model/AuditingParameterModel";

const auditingParameterSchema = Yup.object().shape({
  title: Yup.string().trim().required("Parameter Title is required"),
  legendId: Yup.string().trim().required("Legend is required"),
});

const AuditingParameterAddEditModal = ({
  auditingParameterAddEditModalOpen,
  setAuditingParameterAddEditModalOpen,
  itemId,
  getAuditingParameterList,
  formData,
  setFormData,
  auditingParameterDetails,
}) => {
  const [legendOptions, setLegendOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const closeModal = () => {
    setAuditingParameterAddEditModalOpen(false);
    setErrors({});
    setTouched({});
  };

  const validateSingleField = async (field, value) => {
    try {
      await Yup.reach(auditingParameterSchema, field).validate(value);
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return true;
    } catch (error) {
      setErrors((prev) => ({ ...prev, [field]: error?.message || "Invalid value" }));
      return false;
    }
  };

  const handleRootChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (auditingParameterSchema.fields[field] && touched?.[field]) {
      await validateSingleField(field, value);
    } else if (errors?.[field]) {
      // Hide existing error when user starts changing value.
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFieldBlur = async (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    await validateSingleField(field, formData?.[field] ?? "");
  };

  const loadLegendOptions = async () => {
    const response = await getLegendsList({});
    const legends = response?.data || [];
    const options = legends.map((legend) => ({
      value: legend?.id || legend?._id || "",
      label: legend?.title || legend?.name || "",
    }));
    setLegendOptions(options);
  };

  const handleSubmit = async () => {
    try {
      await auditingParameterSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      setTouched({ title: true, legendId: true });
      return;
    }

    const params = {
      title: formData?.title,
      legendId: formData?.legendId || null,
      status: formData?.status,
    };

    const response = itemId
      ? await updateAuditingParameter(itemId, params)
      : await addAuditingParameter(params);

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getAuditingParameterList?.();
    } else {
      toast.error(response?.message || "Unable to save auditing parameter");
    }
  };

  useEffect(() => {
    if (itemId && auditingParameterDetails) {
      setFormData({
        title:
          auditingParameterDetails?.title ||
          auditingParameterDetails?.parameterTitle ||
          "",
        legendId:
          auditingParameterDetails?.legendId ||
          auditingParameterDetails?.legend?.id ||
          "",
        status:
          auditingParameterDetails?.status === "active" ||
          auditingParameterDetails?.status === true,
      });
    }
  }, [itemId, auditingParameterDetails]);

  useEffect(() => {
    if (auditingParameterAddEditModalOpen) {
      loadLegendOptions();
    }
  }, [auditingParameterAddEditModalOpen]);

  return (
    <Modal
      isOpen={auditingParameterAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      centered
      className="modal-full-height"
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">
              {itemId ? "Edit" : "Create"} Auditing Parameter
            </h5>
            <Button close onClick={closeModal} />
          </div>

          <hr />
          <Row className="justify-content-between align-items-center">
          <Col md={12} className="mt-2">
              <Label>Parameter Title <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="title"
                value={formData.title || ""}
                onChange={(e) => handleRootChange("title", e.target.value)}
                onBlur={() => handleFieldBlur("title")}
                placeholder="Enter Parameter Title"
              />
              {errors?.title && touched?.title && (
                <small className="text-danger">{errors.title}</small>
              )}
            </Col>
            <Col md={12} className="mt-2">
              <Label>Select Legends <span className="text-danger">*</span></Label>
              <Select
                options={legendOptions}
                className="select2-selection"
                placeholder="Select Legend"
                value={
                  legendOptions.find(
                    (opt) => String(opt.value) === String(formData?.legendId)
                  ) || null
                }
                onChange={(selectedOption) =>
                  handleRootChange("legendId", selectedOption?.value || "")
                }
                onBlur={() => handleFieldBlur("legendId")}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {errors?.legendId && touched?.legendId && (
                <small className="text-danger">{errors.legendId}</small>
              )}
            </Col>
            <Col md={12} className="mt-2">
              <Label>Status</Label>
              <div className="input-box d-flex align-items-center justify-content-between">
                <span className="text-muted">Choose Status</span>
                <div className="d-flex align-items-center">
                  <span className={`me-2 fw-semibold ${formData?.status ? "text-success" : ""}`}>
                    {formData?.status ? "Active" : "Deactive"}
                  </span>
                  <div className="form-switch">
                    <Input
                      type="switch"
                      name="status"
                      defaultChecked={Boolean(formData?.status)}
                      onChange={(e) => handleRootChange("status", e.target.checked)}
                      className="form-check-input-padding"
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              {itemId ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AuditingParameterAddEditModal;
